import React from "react";
import axios from "axios";
import get from "lodash/get";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useLocalStorage } from "react-use";
import { useAuth0 } from "@auth0/auth0-react";
import Typography from "@mui/material/Typography";
import { PageLoader } from "app/components/page-loader";
import { colors, customBreakpointsMax } from "app/theme";
import { useNavigate, useSearchParams } from "react-router-dom";
import { socialAuth, SocialAuthConnectionType } from "app/utils/socialAuth";
import { getSignInMethods } from "app/utils/signInButtons";
import { useCMSData } from "app/hooks/useCMSData";

export const UserInvitationCallback: React.FC = () => {
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const { isAuthenticated, logout, loginWithRedirect } = useAuth0();
  const setStoredInvitation = useLocalStorage("invitation-data", null)[1];

  const cmsData = useCMSData({ returnData: true });

  const SignInMethods = React.useMemo(
    () => getSignInMethods(cmsData),
    [cmsData]
  );

  const [loading, setLoading] = React.useState(true);
  const [invitationAlreadyAccepted, setInvitationAlreadyAccepted] =
    React.useState(false);

  const getUserInvitation = async () => {
    await axios
      .get(
        `${process.env.REACT_APP_API}/user-invitation?token=${searchParams.get(
          "token"
        )}`
      )
      .then((response) => {
        const inv = get(response, "data[0]", {});
        if (inv && inv.pending === false) {
          setInvitationAlreadyAccepted(true);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log("Error fetching user invitation:", error);
      });
  };

  const handleSignInButtonClick =
    (type: SocialAuthConnectionType | "email-otp") => () => {
      setLoading(true);
      if (type === "email-otp") {
        loginWithRedirect({
          authorizationParams: {
            connection: "email",
            screen_hint: "login",
          },
        });
      } else {
        socialAuth(type, {});
      }
    };

  React.useEffect(() => {
    const token = searchParams.get("token");
    getUserInvitation();
    if (!token) {
      navigate("/404");
      return;
    }
    axios
      .get(
        `${process.env.REACT_APP_API}/user-invitation?token=${token}&pending=true`
      )
      .then((response) => {
        const { data } = response;
        if (data && data.length > 0) {
          const invitation = data[0];
          setStoredInvitation(invitation);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log("Error fetching user invitation:", error);
        navigate("/404");
      });
    if (isAuthenticated) {
      logout();
    }
  }, [isAuthenticated]);

  return (
    <Box>
      {loading && <PageLoader />}
      {!loading && (
        <Box
          sx={{
            gap: "20px",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography>
            {invitationAlreadyAccepted
              ? "This invitation has already been accepted."
              : "To accept the invitation, please sign in."}
          </Typography>
          {!invitationAlreadyAccepted && (
            <Box
              sx={{
                gap: "30px",
                display: "flex",
                width: "max-content",
                flexDirection: "row",
                [customBreakpointsMax.mobile]: {
                  alignItems: "center",
                  flexDirection: "column",
                },
                "> button": {
                  gap: "10px",
                  width: "190px",
                  height: "32px",
                  display: "flex",
                  fontSize: "12px",
                  fontWeight: "700",
                  borderRadius: "5px",
                  alignItems: "center",
                  textTransform: "none",
                  justifyContent: "center",
                  color: colors.primary.white,
                  background: colors.primary.blue,
                  ":hover": {
                    background: colors.shades.blue[400],
                  },
                },
              }}
            >
              {SignInMethods.map((method) => (
                <Button
                  key={method.title}
                  startIcon={method.icon}
                  data-cy="sign-in-method-button"
                  onClick={handleSignInButtonClick(method.type)}
                >
                  {method.title}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
